// The curated default detection rules for popular titles (F-CAT-04; spec/10 §7.3 "Defaults").
//
// Most catalogue games came in from IGDB with no detection rules (`processNames: []`), and a title launched through
// a launcher URI (`steam://rungameid/570`) has no exe recipe to fall back on — so a station tracked none of them
// (2026-10-01: "tracking 0 title(s)"). This table is what a well-known title's own game process is called — the game,
// never its launcher — so:
//
//   - the api's one-shot backfill (drizzle/0074) filled the catalogue's EMPTY `detection.processNames` from it;
//   - HQ's Detection sheet offers it as "Use suggested" on a title that has none;
//   - the station (station/Core/Library/Play/DefaultProcessNames.cs, a mirror of this table kept in step by
//     `default-process-names.test.ts`) uses it for a title whose bundle carries no rules at all.
//
// Keys are title names as `normaliseTitleName` reads them (lower case, letters and digits only, so
// "Call of Duty®: Warzone™" and "call of duty warzone" are one key). Process names are stored as detection stores
// them: lower case, no `.exe`. A `regex` narrows the names (every rule a title sets must hold, spec/10 §7.3) —
// Minecraft's `javaw` is only Minecraft when it runs from a Minecraft folder.
//
// Plain values only (no Zod), so `@clavian/contracts/constants` re-exports it for the console without pulling a
// route into the bundle.
/** The curated defaults, one row per title family. Keep `DefaultProcessNames.cs` (station) in step. */
export const DEFAULT_TITLE_DETECTIONS = [
    { names: ['Dota 2'], processNames: ['dota2'] },
    { names: ['Counter-Strike 2'], processNames: ['cs2'] },
    { names: ['Counter-Strike: Global Offensive'], processNames: ['csgo'] },
    { names: ['VALORANT'], processNames: ['valorant-win64-shipping', 'valorant'] },
    { names: ['Apex Legends'], processNames: ['r5apex', 'r5apex_dx12'] },
    { names: ['Call of Duty: Warzone', 'Call of Duty', 'Call of Duty: Modern Warfare III', 'Call of Duty: Black Ops 6'], processNames: ['cod', 'modernwarfare'] },
    { names: ['Fortnite'], processNames: ['fortniteclient-win64-shipping', 'fortniteclient-win64-shipping_eac_eos', 'fortniteclient-win64-shipping_be'] },
    { names: ['Path of Exile 2', 'Path of Exile'], processNames: ['pathofexile', 'pathofexile_x64', 'pathofexilesteam', 'pathofexile_x64steam', 'pathofexile_kg', 'pathofexile_x64_kg'] },
    { names: ['Grand Theft Auto V', 'GTA V', 'Grand Theft Auto V Enhanced', 'Grand Theft Auto V Legacy'], processNames: ['gta5', 'gta5_enhanced'] },
    { names: ['PUBG: Battlegrounds', "PlayerUnknown's Battlegrounds", 'PUBG'], processNames: ['tslgame'] },
    { names: ['Rocket League'], processNames: ['rocketleague'] },
    { names: ['League of Legends'], processNames: ['league of legends'] },
    { names: ['Overwatch 2', 'Overwatch'], processNames: ['overwatch'] },
    { names: ['Minecraft', 'Minecraft: Java Edition', 'Minecraft: Bedrock Edition'], processNames: ['javaw', 'minecraft.windows'], regex: 'minecraft' },
    { names: ['Roblox'], processNames: ['robloxplayerbeta'] },
    { names: ['Genshin Impact'], processNames: ['genshinimpact', 'yuanshen'] },
    { names: ["Tom Clancy's Rainbow Six Siege", 'Rainbow Six Siege', 'Rainbow Six Siege X'], processNames: ['rainbowsix', 'rainbowsix_vulkan', 'rainbowsix_be'] },
    { names: ['Marvel Rivals'], processNames: ['marvel-win64-shipping'] },
    { names: ['Rust'], processNames: ['rustclient'] },
    { names: ['Destiny 2'], processNames: ['destiny2'] },
    { names: ['The Finals'], processNames: ['discovery'] },
    { names: ['EA Sports FC 25'], processNames: ['fc25'] },
    { names: ['EA Sports FC 24'], processNames: ['fc24'] },
    { names: ['Elden Ring'], processNames: ['eldenring'] },
    { names: ['Cyberpunk 2077'], processNames: ['cyberpunk2077'] },
    { names: ['Warframe'], processNames: ['warframe.x64'] },
    { names: ['Hearthstone'], processNames: ['hearthstone'] },
    { names: ['World of Warcraft'], processNames: ['wow'] },
    { names: ['Dead by Daylight'], processNames: ['deadbydaylight-win64-shipping'] },
    { names: ['Palworld'], processNames: ['palworld-win64-shipping'] },
    { names: ['Helldivers 2'], processNames: ['helldivers2'] },
    { names: ['Among Us'], processNames: ['among us'] },
    { names: ['Fall Guys'], processNames: ['fallguys_client_game', 'fallguys_client'] },
    { names: ['Brawlhalla'], processNames: ['brawlhalla'] },
    { names: ['War Thunder'], processNames: ['aces'] },
    { names: ['World of Tanks'], processNames: ['worldoftanks'] },
    { names: ['Escape from Tarkov'], processNames: ['escapefromtarkov'] },
    { names: ['Battlefield 2042'], processNames: ['bf2042'] },
    { names: ['Lethal Company'], processNames: ['lethal company'] },
    { names: ['Sea of Thieves'], processNames: ['sotgame'] },
    { names: ['Valheim'], processNames: ['valheim'] },
    { names: ['Terraria'], processNames: ['terraria'] },
    { names: ['Stardew Valley'], processNames: ['stardew valley'] },
    { names: ['Forza Horizon 5'], processNames: ['forzahorizon5'] },
    { names: ['Naraka: Bladepoint'], processNames: ['narakabladepoint'] },
    { names: ['Tekken 8'], processNames: ['tekken8'] },
    { names: ['Street Fighter 6'], processNames: ['streetfighter6'] },
    { names: ['Mortal Kombat 1'], processNames: ['mk12'] },
    { names: ['Black Myth: Wukong'], processNames: ['b1-win64-shipping'] },
    { names: ["Baldur's Gate 3"], processNames: ['bg3', 'bg3_dx11'] },
    { names: ['Red Dead Redemption 2'], processNames: ['rdr2'] },
    { names: ['Honkai: Star Rail'], processNames: ['starrail'] },
    { names: ['Team Fortress 2'], processNames: ['tf_win64', 'tf'] },
];
/** A title name as the defaults key it: lower case, letters and digits only ("Call of Duty®: Warzone™" → "callofdutywarzone"). */
export function normaliseTitleName(name) {
    return name
        .replace(/[™®©]/g, '') // ™ would fold to "TM" under NFKD
        .normalize('NFKD')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
}
const BY_NAME = new Map(DEFAULT_TITLE_DETECTIONS.flatMap((row) => row.names.map((name) => [normaliseTitleName(name), row])));
/** The curated default detection for a title called `name`, or `undefined` when there is none. */
export function defaultDetectionFor(name) {
    const key = normaliseTitleName(name);
    return key === '' ? undefined : BY_NAME.get(key);
}
//# sourceMappingURL=default-process-names.js.map